import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, Routes } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { finalize, forkJoin } from 'rxjs';
import _ from 'lodash';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, FilterOption } from '@card-software/library';
import { EndpointAccess } from '../../models/access-catalog/endpoint-access';
import { RoleAction, RoleActionCheck, RoleDescription, RolePage } from '../../models/access-catalog/role-description';
import { AccessType } from '../../models/user-manager/access-type';
import { ACCESS_TYPE_LABELS, AccessTypeLabel, accessTypeLabel } from '../../models/user-manager/access-type-labels';
import { Group } from '../../models/user-manager/group';
import { Role } from '../../models/user-manager/roles';
import { AccessCatalogProxyService } from '../../proxies/access-catalog.proxy.service';
import { GroupProxyService } from '../../proxies/group.proxy.service';
import { RolesProxyService } from '../../proxies/roles.proxy.service';

/** Whether delegated user managers ("external managers") can see and assign a group (#6256). */
type Visibility = 'visible' | 'hidden' | 'global';

/** A UserMaster.UI page and the roles its route allows (any of them). */
interface CatalogPage {
  path: string;
  roles: string[];
  /** The route's own title (its last breadcrumb), when it has one. */
  labelKey: string | null;
  /** The path in words, e.g. "Approval policy details": shown when the route has no title or it isn't translated. */
  fallbackLabel: string;
}

interface CatalogRole {
  role: Role;
  /** From access-catalog.json; null when the role isn't described yet. */
  description: RoleDescription | null;
  pages: CatalogPage[];
  externalPages: RolePage[];
  /** UserMaster.API endpoints whose [RequireRole] names this role (shown when the role isn't described). */
  endpoints: EndpointAccess[];
}

interface CatalogGroup {
  group: Group;
  visibility: Visibility;
  roles: CatalogRole[];
  /** What a user in this group can do, from its roles' descriptions, once each. */
  summary: string[];
  matchedRoles: string[];
}

/** How each enforcement state reads in the page. */
const CHECK_LABELS: Record<
  RoleActionCheck,
  { key: string; fallback: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' }
> = {
  Attribute: { key: 'AccessCatalog_Check_Attribute', fallback: 'API checks the role', severity: 'success' },
  Service: { key: 'AccessCatalog_Check_Service', fallback: 'API checks it in the service', severity: 'info' },
  None: { key: 'AccessCatalog_Check_None', fallback: 'UI only', severity: 'warn' },
  MissingEndpoint: { key: 'AccessCatalog_Check_Missing', fallback: 'Endpoint not found', severity: 'danger' },
  OtherApi: { key: 'AccessCatalog_Check_OtherApi', fallback: 'Other API', severity: 'secondary' },
};

/** Filter values: the dynamic filter only carries strings and numbers. */
interface CatalogFilters {
  search: string;
  type: AccessType | null;
  visibility: Visibility | null;
}

/**
 * The access catalog (#6265): what each group allows, through its roles, in UserMaster.UI pages and UserMaster.API
 * endpoints. Endpoints come from the API's own attributes and pages from this app's routes, so both match the code.
 */
@Component({
  selector: 'app-access-catalog',
  templateUrl: './access-catalog.component.html',
})
export class AccessCatalogComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly translateService = inject(TranslateService);
  private readonly groupProxyService = inject(GroupProxyService);
  private readonly rolesProxyService = inject(RolesProxyService);
  private readonly accessCatalogProxyService = inject(AccessCatalogProxyService);

  isLoading = signal(0);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'access-catalog-filters',
    config: this.filterConfig,
  });

  private readonly groups = signal<Group[]>([]);
  private readonly roles = signal<Role[]>([]);
  readonly endpoints = signal<EndpointAccess[]>([]);
  private readonly descriptions = signal<RoleDescription[]>([]);
  private readonly pages = this.readPages(this.router.config);
  private readonly filters = signal<CatalogFilters>({ search: '', type: null, visibility: null });

  /** Each described role under its name and its aliases. */
  private readonly descriptionByName = computed(
    () => new Map(this.descriptions().flatMap((d) => [d.name, ...d.aliases].map((name) => [name, d] as const))),
  );

  readonly catalog = computed<CatalogGroup[]>(() => {
    const roleById = new Map(this.roles().map((role) => [role.id, role]));
    return _.sortBy(this.groups(), (g) => (g.name ?? '').toLowerCase()).map((group) => {
      const roles = (group.roleIds ?? []).flatMap((id) => {
        const role = roleById.get(id);
        return role ? [this.catalogRole(role)] : [];
      });
      return {
        group,
        visibility: this.visibilityOf(group),
        roles,
        summary: _.uniq(roles.flatMap((r) => r.description?.actions.map((a) => a.does) ?? [])),
        matchedRoles: [],
      };
    });
  });

  // ---- Where the description and the data or code disagree

  /** Described roles that no role in the data is called (by name or alias). */
  readonly describedButMissing = computed(() => {
    const names = new Set(this.roles().map((r) => r.name));
    return this.descriptions().filter((d) => ![d.name, ...d.aliases].some((n) => names.has(n)));
  });

  /** Actions whose endpoint the API doesn't have. */
  readonly missingEndpoints = computed(() =>
    this.descriptions().flatMap((d) => d.actions.filter((a) => a.check === 'MissingEndpoint').map((a) => ({ role: d.name, action: a }))),
  );

  /** Roles in the data with no description yet (other domains are described later). */
  readonly undescribedCount = computed(() => this.roles().filter((r) => !this.descriptionByName().has(r.name ?? '')).length);

  readonly filteredGroups = computed(() => {
    const { search, type, visibility } = this.filters();
    const words = search.split(/\s+/).map((w) => this.searchable(w)).filter((w) => w);
    return this.catalog().flatMap((entry) => {
      if (type !== null && (entry.group.type ?? AccessType.Standard) !== type) return [];
      if (visibility !== null && entry.visibility !== visibility) return [];
      if (!words.length) return [entry];
      const matchedRoles = entry.roles.filter((r) => this.roleMatches(r, words)).map((r) => r.role.name ?? '');
      const groupName = this.searchable(entry.group.name ?? '');
      if (!matchedRoles.length && !words.every((w) => groupName.includes(w))) return [];
      return [{ ...entry, matchedRoles }];
    });
  });

  readonly filteredEndpoints = computed(() => {
    const text = this.filters().search.toLowerCase();
    return this.endpoints().filter(
      (e) =>
        !text || e.route.toLowerCase().includes(text) || e.requiredRoles.some((anyOf) => anyOf.some((r) => r.toLowerCase().includes(text))),
    );
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.applyFilters(filters));

    effect(
      () => {
        if (this.translateService.currentLang) this.initializeFilterConfig();
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.isLoading.update((v) => v + 1);
    forkJoin({
      groups: this.groupProxyService.getAllGroups(),
      roles: this.rolesProxyService.getAllRoles(),
      endpoints: this.accessCatalogProxyService.getEndpoints(),
      descriptions: this.accessCatalogProxyService.getRoles(),
    })
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe(({ groups, roles, endpoints, descriptions }) => {
        this.groups.set(groups.data ?? []);
        this.roles.set(roles.data ?? []);
        this.endpoints.set(endpoints.data ?? []);
        this.descriptions.set(descriptions.data ?? []);
      });
  }

  // ---- Display

  typeLabel(type: AccessType | undefined): AccessTypeLabel {
    return accessTypeLabel(type);
  }

  checkLabel(action: RoleAction): { key: string; fallback: string; severity: 'success' | 'info' | 'warn' | 'danger' | 'secondary' } {
    return CHECK_LABELS[action.check];
  }

  /** "A or B", and "(A or B) and C" when several [RequireRole] apply. */
  rolesLabel(endpoint: EndpointAccess): string {
    const parts = endpoint.requiredRoles.map((anyOf) => anyOf.join(` ${this.translate('AccessCatalog_Or', 'or')} `));
    return parts.length > 1 ? parts.map((p) => `(${p})`).join(` ${this.translate('AccessCatalog_And', 'and')} `) : (parts[0] ?? '');
  }

  methodSeverity(method: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
    switch (method) {
      case 'GET':
        return 'success';
      case 'POST':
        return 'info';
      case 'PUT':
      case 'PATCH':
        return 'warn';
      case 'DELETE':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  catalogGroup(row: unknown): CatalogGroup {
    return row as CatalogGroup;
  }

  endpointRow(row: unknown): EndpointAccess {
    return row as EndpointAccess;
  }

  // ---- Building the catalog

  private catalogRole(role: Role): CatalogRole {
    const name = role.name ?? '';
    const description = this.descriptionByName().get(name) ?? null;
    const names = description ? [description.name, ...description.aliases] : [name];
    return {
      role,
      description,
      pages: this.pages.filter((page) => page.roles.some((r) => names.includes(r))),
      externalPages: description?.pages ?? [],
      endpoints: this.endpoints().filter((e) => e.requiredRoles.some((anyOf) => anyOf.includes(name))),
    };
  }

  private searchable(value: string): string {
    return _.deburr(value).toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private roleMatches(catalogRole: CatalogRole, words: string[]): boolean {
    const description = catalogRole.description;
    const values = [
      catalogRole.role.name ?? '',
      description?.description ?? '',
      ...(description?.actions.flatMap((a) => [a.does, a.route]) ?? []),
      ...catalogRole.pages.flatMap((p) => [p.path, p.fallbackLabel]),
      ...catalogRole.externalPages.flatMap((p) => [p.path, p.label]),
    ];
    const searchableValues = values.map((value) => this.searchable(value));
    return words.every((word) => searchableValues.some((value) => value.includes(word)));
  }

  private visibilityOf(group: Group): Visibility {
    if (group.type === AccessType.Global) return 'global';
    return group.isDelegable ? 'visible' : 'hidden';
  }

  /** Every route of this app that lists roles, with its full path. */
  private readPages(routes: Routes, parent = ''): CatalogPage[] {
    return routes.flatMap((route) => {
      const path = [parent, route.path].filter((p) => p).join('/');
      const roles = route.data?.['roles'] as string[] | undefined;
      const breadcrumb = route.data?.['breadcrumb'] as { label?: string }[] | undefined;
      const own: CatalogPage[] = roles?.length
        ? [{ path: `/${path}`, roles, labelKey: _.last(breadcrumb)?.label ?? null, fallbackLabel: _.upperFirst(_.lowerCase(path)) }]
        : [];
      return [...own, ...this.readPages(route.children ?? [], path)];
    });
  }

  // ---- Filters

  private translate(key: string, fallback: string): string {
    const translation = this.translateService.instant(key);
    return translation === key ? fallback : translation;
  }

  private initializeFilterConfig(): void {
    const typeOptions: FilterOption[] = ACCESS_TYPE_LABELS.map((label) => ({
      label: this.translate(label.key, label.fallback),
      value: label.value,
    }));
    const visibilityOptions: FilterOption[] = [
      { label: this.translate('UserDetails_ExternalManagers_Visible', 'Visible'), value: 'visible' },
      { label: this.translate('UserDetails_ExternalManagers_Hidden', 'Hidden'), value: 'hidden' },
      { label: this.translate('UserDetails_ExternalManagers_NotAllowed', 'Not allowed (Global)'), value: 'global' },
    ];

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'search',
          label: this.translate('AccessCatalog_Filter_Search', 'Search'),
          placeholder: this.translate('AccessCatalog_Filter_Search_Placeholder', 'Group, role, action or route'),
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'type',
          label: this.translate('UserDetails_Table_Type', 'Type'),
          options: typeOptions,
          showClear: true,
          defaultVisible: true,
        },
        {
          type: 'select',
          formControlName: 'visibility',
          label: this.translate('UserDetails_Table_ExternalManagers', 'External managers'),
          options: visibilityOptions,
          showClear: true,
          defaultVisible: true,
        },
      ],
    });
  }

  private applyFilters(values: DynamicFilterValues): void {
    const search = values['search'];
    const type = values['type'];
    const visibility = values['visibility'];
    this.filters.set({
      search: typeof search === 'string' ? search.trim() : '',
      type: _.isNil(type) || type === '' ? null : (Number(type) as AccessType),
      visibility: visibility === 'visible' || visibility === 'hidden' || visibility === 'global' ? visibility : null,
    });
  }
}
